import {extractHighlightedWellsLiteralCoordinatesFromUrl, safeText} from "../util/CommonUtils.js";
import Batch from "../entities/Batch.js";
import EventConsumer from "../entities/event-bus/EventConsumer.js";
import ImportVisualizationsDialog from "../components/dialog/ImportVisualizationsDialog.js";
import StructureMetaList from "../entities/StructureMetaList.js";
import StructureMeta from "../entities/StructureMeta.js";
import FormValidationError from "../entities/error/FormValidationError.js";
import TableColumnsFactory from "../components/visualization/TableColumnsFactory.js";
import PeaksAsRowsColumnsFactory from "../components/visualization/PeaksAsRowsColumnFactory.js";
import {VisualisationComponent} from "../components/visualization/VisualisationComponent.js";
import TwoButtonsDialog from "../components/dialog/TwoButtonsDialog.js";
import VisualizationProperties from "../components/visualization/VisualizationProperties.js";
import SubstanceAliasDialog from "../components/dialog/SubstanceAliasDialog.js";
import AliasColor from "../entities/AliasColor.js";
import SubstanceJson from "../entities/SubstanceJson.js";
import MouseBlockingUnderlay from "../components/MouseBlockingUnderlay.js";
import SubstanceDialogFormData from "../../components/SubstanceDialog/SubstanceDialogFormData.js";
import BaseDialogCmp from "../../components/BaseDialog/BaseDialogCmp.js";
import SubstanceExtractionParams from "../../components/massSpec/SubstanceExtractionParams.js";
import ResourceBulkAction from "../../components/ResourceBulkAction/ResourceBulkAction.js";
import AccessLevel from "../entities/AccessLevel.js";
import {createOrgNameUrlTemplate, extractOrgNameFromUrl} from "../util/UrlUtils.js";
import UpdateAccessDialogCmp from "../../components/UpdateAccessDialog/UpdateAccessDialogCmp.js";
import ResourcePermission from "../components/permission/ResourcePermission.js";
import PermissionOnResourceError from "../entities/error/PermissionOnResourceError.js";
import FailedBatchResources
	from "../../components/DeleteBatchNotificationDialog/FailedBatchResourses/FailedBatchResources.js";
import DeleteBatchNotificationDialogCmp
	from "../../components/DeleteBatchNotificationDialog/DeleteBatchNotificationDialogCmp.js";
import FailedInjectionsDialog
	from "../../components/DeleteBatchNotificationDialog/FailedBatchResourses/FailedInjectionsDialog.js";
import PageIterator from "../../pages/PageIterator.js";
import BatchViewCmp from "../../components/BatchViewCmp/BatchViewCmp.js";

export class BatchPageController extends EventConsumer {
	/** @type BatchPageIoC */
	#ioc;
	/** @type {Org} */
	#org;
	#tableDataConverters;
	/** @type BaseDialogCmp */
	#accessDeniedPopup;
	/** @type BaseDialogCmp */
	#genericErrorPopup;
	/** @type {BatchMeta} */
	#currentBatchMeta;
	/** @type {BatchPageHeaderCmp} */
	#pageHeader;
	/** @type {DeleteBatchNotificationDialogCmp|UpdateAccessDialogCmp} */
	#actionDialog;
	/** @type {SecurityContext} */
	#securityContext;


	constructor(aliasColorScheme, visGrid, ioc, securityContext, batchMeta, org) {
		super();
		this.#ioc = ioc;
		this.#securityContext = securityContext;
		this._aliasColorScheme = aliasColorScheme;
		this._visGrid = visGrid;
		document.title = batchMeta.name;
		this.#org = org;
		this.#pageHeader = this.#ioc.getPageHeaderCmp();
		this.#pageHeader.onShareBatchClick(this.#onShareBatchClick.bind(this));
		this.#pageHeader.onDeleteBatchClick(this.#onDeleteBatchClick.bind(this));
		this.#pageHeader.fillBreadcrumbs(batchMeta);
		this.#tableDataConverters = {
			svg: async (uri) => {
				if (typeof uri === "string" && !uri.length)
					return Promise.resolve('');
				try {
					return await this.#ioc.getStructureSvgLoader().getByUrlPath(uri);
				} catch (err){
					// if we get an error (from BE or validation js error) we show "no structure" template in viz table
					return Promise.resolve(`
					<span class="material-symbols-outlined v-table__unmatched-icon">warning</span>
					<span>Bad structure</span>
					`)
				}
			}
		};
		this._selection = this.#ioc.getVizDataSelection();
		this._header = new BatchPageHeaderComponent(document.querySelector('.page-header'));
		this._header.on('download-report', ()=>{
			this.#ioc.getBatchRepository().downloadReport(this.#currentBatchMeta.id);
		});
		this._header.on('add-visualization', ()=>{
			this.showAddVisualizationDialog();
		});
		this._header.on('import-visualizations', async ()=>{
			const batchRepo = this.#ioc.getBatchRepository();
			let pageIterator = new PageIterator(batchRepo.getBatchesByName.bind(batchRepo, this.#org.id, ''));
			const batches = await pageIterator.getNext();
			let dialog = new ImportVisualizationsDialog();
			dialog.render();
			dialog.setBatchList(batches);

			dialog.on('batch-name-change', async (name)=> {
				pageIterator = new PageIterator(batchRepo.getBatchesByName.bind(batchRepo, this.#org.id, name));
				const batches = await pageIterator.getNext();
				dialog.setBatchList(batches);
			});

			dialog.on('batch-list-next-page', async ()=> {
				const batches = await pageIterator.getNext();
				dialog.appendBatchList(batches)
			});

			dialog.on('batch-selected', async (batch) => {
				let meta = await this.#ioc.getBatchRepository().getMeta(batch.getId());
				let padding = this._visGrid.getLastRowIndex() + 1;
				for (const visualization of meta.visualizations) {
					let loc = visualization.card.location.split(":");
					visualization.card.location = parseInt(loc[0]) + padding + ":" + loc[1]
				}
				this._visGrid.createPlaceholders(meta.visualizations.filter(vis => vis.card.location !== null).map(vis => {
						let str = vis.card.location.split(":");
						return {location: {row: parseInt(str[0]), col: parseInt(str[1])}}
					})
				)
				dialog.remove();
				for (const visualization of meta.visualizations) {
					await this.createVisualizationFromProperties(this._visGrid, visualization, this.#tableDataConverters)
				}
				await this._visGrid.saveProperties();
			})
		});
		this._header.on('open-injections', () => {
			const org = createOrgNameUrlTemplate(extractOrgNameFromUrl());
			const batchViewCmp = this.#ioc.getBatchViewCmp();
			if (!batchViewCmp.isLoading() && this._selection.size() > 0) {
				let highlighted = '#highlight=' + batchViewCmp.getHighlightedWellsAsString();
				document.location.hash = highlighted;
				document.location.assign(`${org}/injection/${batchViewCmp.getFirstHighlightedInjectionId()}${highlighted}`);
			} else {
				const firstInjectionId = batchViewCmp.getFirstInjectionId();
				if (firstInjectionId != null)
					document.location.assign(`${org}/injection/${firstInjectionId}`);
			}
		});

		this._selection.addListener({
			selectionChanged: (sel) => {
				const totalSelected = sel.size();
				this._header.updateOpenInjectionsButtonText(totalSelected);
				const addBtn = document.getElementById('addSubstanceBtn');
				if (addBtn)
					addBtn.disabled = totalSelected === 0;
			}
		});

		const substanceBarCmp = this.#ioc.getSubstanceBarCmp();
		substanceBarCmp.onSubstanceSelected((substanceId, name) => {
			this._selection.clear();
			let substanceName = name.toLowerCase();
			for (const injection of this.batch.getInjections()) {
				let peaks = injection.peaks.filter(p => p.substance.displayName.toLowerCase() === substanceName).map(p => p.id);
				if (peaks.length > 0)
					this._selection.add(injection.getId(), new Set(peaks))
			}
			this._selection.flush();
		});
		substanceBarCmp.onSubstanceDeselected(()=>{
			this._selection.clear();
			this._selection.flush();
		})
		substanceBarCmp.onEditSubstanceClick((_, name, color) => {
			this.substanceBarItemEditBtnClick(name, color)
		});
		substanceBarCmp.onAddSubstanceClick(async () => {
			const dialog = this.#createSubstanceDialog();
			const substance = new SubstanceJson();
			await dialog.show(SubstanceDialogFormData.build({
				substance: substance,
				aliasAutocompleteList: this.batch.getAliases(),
				massSpecEnabled: false,
				readonly: false
			}), SubstanceExtractionParams.default(substance));
		});
		const batchViewCmp = this.#ioc.getBatchViewCmp();

		batchViewCmp.onTableRowClick((rowIndex, _, rowObjectId, rowSelector, evt) => {
			this.#fillSelectionAndNotify(rowIndex, evt, rowSelector)
		});
		this.#accessDeniedPopup = new BaseDialogCmp(
			{title: "Access denied", btnCancel: "Close", btnOk: "Refresh"});
		this.#accessDeniedPopup.onOk(() => window.location.reload());
		this.#genericErrorPopup = new BaseDialogCmp({title: "Error", btnCancel: "Close", btnOk: "Refresh"});
		this.#accessDeniedPopup.onOk(() => window.location.reload());
	}

	substanceBarItemEditBtnClick(name, color) {
		let batchColorPreset = this._aliasColorScheme.getBatchColorPreset();
		let dialog = new SubstanceAliasDialog(this._aliasColorScheme, batchColorPreset);
		let sourceAlias = new AliasColor(name, color);
		dialog.render(sourceAlias, {readOnly: !this.#currentBatchMeta.isWritable()});

		dialog.on('ok', async (formData) => {
			const replacement = new AliasColor(formData.alias, formData.color);
			try {
				const bulkAction = new ResourceBulkAction(this.batch.getInjectionsMap());
				const injectionsWithSubstanceAlias = this.batch.getInjectionIdsContainingSubstance(sourceAlias.alias);
				await bulkAction.checkAccessLevelAndPerform(injectionsWithSubstanceAlias, AccessLevel.WRITE,
					async (_injectionsWithAccess) => {
						await this.#ioc.getBatchRepository().editBatchAliasColor(this.#currentBatchMeta.id, sourceAlias.alias, replacement)
						batchColorPreset.replace(sourceAlias, replacement);
						dialog.remove()
						document.location.reload();
					});
			} catch (err) {
				if (err instanceof FormValidationError)
					dialog.showAliasErrorMsg(err.message);
				else
					throw err;
			}
		})

		dialog.on('delete', async () => {
			const injectionsWithSubstanceAlias = this.batch.getInjectionIdsContainingSubstance(sourceAlias.alias);
			await new ResourceBulkAction(this.batch.getInjectionsMap())
				.checkAccessLevelAndPerform(injectionsWithSubstanceAlias, AccessLevel.WRITE,
					async (injectionsWithAccess) => {
						await this.#ioc.getSubstanceRepository()
							.deleteSubstanceFromInjections(injectionsWithAccess, sourceAlias.alias)
						dialog.remove()
						document.location.reload();
					});

		})
	}

	/**
	 * @param {number} rowIndex
	 * @param {MouseEvent} evt
	 * @param {RowSelector_v2} rowSelector
	 */
	#fillSelectionAndNotify(rowIndex, evt, rowSelector){
		const isRowAlreadySelected = rowSelector.isSelected(rowIndex);
		const {added, removed} = rowSelector.getSelectionAfterClick(
			rowIndex, evt, isRowAlreadySelected);
		if (removed == null) {
			this._selection.clear();
		} else {
			for (const [injId, peakIdsSet] of removed) {
				this._selection.remove(injId, peakIdsSet);
			}
		}
		for (const [injId, peakIdsSet] of added) {
			this._selection.add(injId, peakIdsSet);
		}
		this._selection.flush({target: rowSelector});
	}

	/** @param {string} id */
	#onDeleteBatchClick(id){
		this.#actionDialog = new DeleteBatchNotificationDialogCmp();
		this.#actionDialog.onOkCb(async (action) => {
			await this.#deleteBatch(id, action);
		});
		this.#actionDialog.show(1);
	}

	/**
	 * @param {string} id
	 * @param {string} actOnNested we can delete or unassign injections in batch
	 * @returns {Promise<void>}
	 */
	async #deleteBatch(id, actOnNested){
		try {
			await this.#ioc.getBatchRepository().deleteBatches([id], actOnNested);
			window.location.href = `${createOrgNameUrlTemplate(extractOrgNameFromUrl())}/batches`
		} catch (err) {
			if (err.isForbidden){
				const failedResource = FailedBatchResources.parseServerJsonMany(err.response);
				// we check only first failed resource, because this is batch page and try to delete only one resource
				if (!failedResource[0].isBatchFailed())
					this.#openFailedBatchResourcesDialog(failedResource)
				else throw err
			} else {
				throw err
			}
		}
	}

	/** @param {FailedBatchResources[]} failedResource */
	#openFailedBatchResourcesDialog(failedResource) {
		const dialog = new FailedInjectionsDialog();
		dialog.show(this.#org.name, failedResource);
		dialog.onCancel(this.#actionDialog.toggleProgressBarVisibility.bind(this.#actionDialog, false))
	}

	/** @param {BatchMeta} batchMeta */
	async #onShareBatchClick(batchMeta) {
		this.#actionDialog = new UpdateAccessDialogCmp(this.#ioc.getTeamRepository(),
			this.#ioc.getUserRepository(), batchMeta.orgId, this.#securityContext.getCurrentUser().basicUser.id);
		try {
			const resourcePermissions = await this.#ioc.getPermissionRepository()
				.getResourcePermissions(ResourcePermission.RESOURCE_TYPE.BATCH, [batchMeta.id]);
			this.#actionDialog.show(resourcePermissions);
			this.#actionDialog.onSaveCb(this.#savePermissions.bind(this))
		} catch (err) {
			if (err instanceof PermissionOnResourceError && err.isResourceForbidden()) {
				const newErr = new PermissionOnResourceError(err.resourceId, err.errorCode,
					`You don't have permissions to change sharing options of ${batchMeta.name}`)
				newErr.cause = err;
				throw newErr;
			}
			throw err
		}
	}

	/**
	 * @param {ResourcePermission[]} resourcePermissions
	 * @param {string} actionType
	 */
	async #savePermissions(resourcePermissions, actionType){
		try {
			await this.#ioc.getPermissionRepository().changeResourcePermissions(resourcePermissions, actionType);
		} catch (err) {
			if (err.isForbidden){
				const failedResource = FailedBatchResources.parseServerJsonMany(err.response);
				// we check only first failed resource, because this is batch page and try to delete only one resource
				if (!failedResource[0].isBatchFailed())
					this.#openFailedBatchResourcesDialog(failedResource)
				else throw err
			} else {
				throw err
			}
		}
	}

	/**
	 * @param {BatchMeta} meta
	 * @returns {Promise<void>}
	 */
	async renderPage(meta){
		this.#currentBatchMeta = meta;
		const batchId = meta.id;
		this.#createBatchViewComponent();

		/** @type {VisualizationData} */
		const visualizationData = this.visualizationData = await this.#ioc.getBatchRepository().getVisData(batchId);
		this.defaultColumns = {
			injections: TableColumnsFactory.createInjectionTableColumns(visualizationData.injections),
			peaks: PeaksAsRowsColumnsFactory.createColumns()
		};

		/** @type {Batch} */
		const batch = this.batch = Batch.create(visualizationData.injections);
		const substanceBar = this.#ioc.getSubstanceBarCmp();
		if (this.#currentBatchMeta.isWritable() || (!this.#currentBatchMeta.isWritable() && batch.getAliases().length > 0)){
			substanceBar.show();
			substanceBar.removeAllSubstances();
			for (const alias of batch.getAliases())
				substanceBar.addSubstanceName(alias, this._aliasColorScheme.getColor(alias));
		}
		this.#setBatchViewCmpData(meta, batch);
		this._header.updateOpenInjectionsButtonText(this._selection.size());

		this._visGrid.createPlaceholders(this.#currentBatchMeta.visualizations.filter(vis=>vis.card.location !== null).map(vis=>{
				let str = vis.card.location.split(":");
				return {location: {row: parseInt(str[0]), col: parseInt(str[1])}}
			})
		)

		for (const visualizationProperties of this.#currentBatchMeta.visualizations)
			await this.createVisualizationFromProperties(this._visGrid, visualizationProperties, this.#tableDataConverters);
	}

	async createVisualizationFromProperties(grid, props, tableDataConverters) {
		let cmp = await VisualisationComponent.createFromProperties(props, this.visualizationData[props.card.dataType],
			this.defaultColumns[props.card.dataType], this._selection, tableDataConverters, this._visGrid, this.#ioc.getRegressionApiClient());
		cmp.on('duplicate', async (properties) => {
			// Find and save location of current visualization, because this info is needed to draw visualization card in right place
			// Later we delete this information
			properties.card.parentLocation = grid._locationOf(cmp);
			properties.card.location = null;
			await this.createVisualizationFromProperties(grid, properties, tableDataConverters);
			await grid.saveProperties();
		});
		cmp.on('selection-changed', (data)=>{
			this.#onPlateOrTableSelectionChange(data);
		});
		cmp.onTableRowClick((rowIndex, cellIndex, rowObjectId, rowSelector, evt)=>{
			const activeRowIndex = cmp.getRowIndexWithActiveCell();
			if (activeRowIndex === rowIndex){
				cmp.markCellAsSelected(rowIndex, cellIndex);
				return;
			}

			this.#fillSelectionAndNotify(rowIndex, evt, rowSelector);

			cmp.markCellAsSelected(rowIndex, cellIndex);
			this._visGrid.removeCellSelectionFromNotCurrentVisualisation(cmp)
		});
		grid.addVisualization(cmp);
		return cmp;
	}

	showAddVisualizationDialog(){
		let chooseSourceDialog = new TwoButtonsDialog();
		chooseSourceDialog.render(`<p>Select visualization type</p>
										<div>
											<input type="radio" name="sourceSelector" id="injections" checked>
											<label for="injections">Injections as rows</label>
										</div>
										<div>
											<input type="radio" name="sourceSelector" id="peaks">
											<label for="peaks">Peaks as rows</label>
											<div id="peaks-filter" class="visually-hidden" style="margin: 1rem 2rem">
												<label for="filter-assigned" style="display: block; height: 24px">
													<input type="checkbox" 
														   id="filter-assigned"
														   title="Peaks that are associated with analytes" 
														   checked>
													Assigned to analyte
												</label>
												<label for="filter-unknown" style="display: block; height: 24px">
													<input type="checkbox"
													 	   id="filter-unknown"
														   title="Peaks were detected, but not assigned to any analyte">
													Unknown peaks
												</label>
											</div>
										</div>`, 'select-visualisation-source-dialog');
		const injectionsOption = chooseSourceDialog._dialog.querySelector('#injections');
		const peaksOption = chooseSourceDialog._dialog.querySelector('#peaks');
		const filterAssignedOption = chooseSourceDialog._dialog.querySelector('#filter-assigned');
		const filterUnknownOption = chooseSourceDialog._dialog.querySelector('#filter-unknown');
		injectionsOption.focus();
		[injectionsOption, peaksOption].forEach(radio => radio.addEventListener('change', () => {
			chooseSourceDialog._dialog.querySelector('#peaks-filter').classList.toggle('visually-hidden', !peaksOption.checked);
		}));
		[filterAssignedOption, filterUnknownOption]
			.forEach(checkbox => checkbox.addEventListener('change', (evt) => {
				if (!filterAssignedOption.checked && !filterUnknownOption.checked)
					evt.target.checked = true;
			}));

		chooseSourceDialog.on('ok', async () => {
			const type = chooseSourceDialog._dialog.querySelector('input[name="sourceSelector"]:checked').id;
			const props = VisualizationProperties.createDefault(type);
			if (type === 'peaks' && filterAssignedOption.checked + filterUnknownOption.checked === 1)
				props.table.rowFilter = `peak.isUnknown == ${filterUnknownOption.checked}`;
			const component = await this.createVisualizationFromProperties(this._visGrid, props, this.#tableDataConverters);
			component.showEditTableDialog(0);
			await this._visGrid.saveProperties();
		})
	}

	_startSelectingInjections(substanceCount){
		this._applySelectInjectionsToAddSubstanceMode(substanceCount);
		return new Promise((resolve, reject) => {
			document.getElementById('cancelSelectionBtn').addEventListener('click', ()=>{
				this._applyDefaultMode();
				reject('cancel');
			})
			const addBtn = document.getElementById('addSubstanceBtn');
			addBtn.addEventListener('click', async () => {
				const injectionIds = this._selection.getInjectionIds();
				await new ResourceBulkAction(this.batch.getInjectionsMap())
					.checkAccessLevelAndPerform(injectionIds, AccessLevel.WRITE,
					(injectionsWithAccess) => {
						addBtn.disabled = true;
						resolve(injectionsWithAccess)
					},
					() =>{
						addBtn.disabled = false;
					});
			});
			addBtn.disabled = this._selection.size() === 0;
		});
	}

	#onPlateOrTableSelectionChange(data) {
		if (data.removed === undefined)
			this._selection.clear()
		else
			data.removed.forEach((v, k) => {
				this._selection.remove(k, v)
			})
		data.added.forEach((v, k) => {
			if (!v || v instanceof Set && v.size === 0)
				v = new Set(this.batch.getInjectionById(k).peaks.map(p => p.id))
			this._selection.add(k, v)
		})
		this._selection.flush({target: data.target});
		const substanceBarCmp = this.#ioc.getSubstanceBarCmp();
		substanceBarCmp.displayAllSubstancesNormal();
	}

	_reloadPage(){
		const highlighted = '#highlight=' + this.#ioc.getBatchViewCmp().getHighlightedWellsAsString();
		document.location.hash = highlighted;
		document.location.assign(`/batch/${this.#currentBatchMeta.id}${highlighted}`)
		document.location.reload();
	}

	_applySelectInjectionsToAddSubstanceMode(substanceCount){
		const customTopBar = document.createElement('div');
		customTopBar.innerHTML = `<div><span>Select injections to add ${substanceCount} substance(s) to</span> 
									<button id="cancelSelectionBtn" class="button button--outlined">Cancel</button>
									<button id="addSubstanceBtn" class="button button--contained">Add</button></div>`
		customTopBar.id = 'custom-top-bar';
		document.body.append(customTopBar);
		this.#ioc.getSubstanceBarCmp().setVisible(false);
		document.body.style.background = 'rgba(0, 0, 0, 0.2)';
	}

	_applyDefaultMode(){
		document.getElementById('custom-top-bar').remove();
		this.#ioc.getSubstanceBarCmp().setVisible(true);
		document.body.style.background = 'var(--md-theme-background)';
	}

	#createSubstanceDialog() {
		const dialog = this.#ioc.createSubstanceDialog();
		dialog.init(this._aliasColorScheme, {orgId: this.#currentBatchMeta.orgId});
		dialog.onDelete(async () => {
			// this should never happen, because dialog doesn't have 'delete' button on this page
			throw new Error('Operation not supported');
		});
		dialog.onSaveOrUpdate(async (substanceExtractionParams) => {
			const substance = substanceExtractionParams.substance;
			const customAliasColor = substance.getSavedColor()
				? new AliasColor(substance.getDisplayName(), substance.getSavedColor())
				: null;
			const structures = new StructureMetaList(
				[new StructureMeta({
					structureId: substance.getStructureId(),
					alias: substance.getAlias(),
					mf: substance.getMolecularFormula(),
					emw: substance.getMonoIsotopicMass()}
				)]);
			dialog.setVisible(false);
			try {
				await this.#addStructuresToInjections(structures, customAliasColor);
			} catch (e) {
				if (e === 'cancel')
					dialog.setVisible(true);
				else {
					dialog.remove();
					throw e;
				}
			}
		});
		dialog.onMultipleStructuresUploaded(async (structures)=>{
			dialog.setVisible(false);
			try {
				await this.#addStructuresToInjections(structures);
			} catch (e) {
				if (e === 'cancel')
					dialog.setVisible(true);
				else {
					dialog.remove();
					throw e;
				}
			}
		});
		return dialog;
	}

	/**
	 * @param {StructureMetaList} structures
	 * @param {AliasColor} [aliasColor]
	 * @returns {Promise<void>}
	 */
	async #addStructuresToInjections(structures, aliasColor) {
		if (structures.isImported())
			return this.#copyAnalytesIntoInjections(structures);
		const requestCreator = new SubstanceRequestCreator(this.batch, this);
		try {
			const req = await requestCreator.create(structures);
			this.#setFullWindowProgressBarVisible(true);
			if (aliasColor){
				await this.#ioc.getBatchRepository().addAliasColor(this.#currentBatchMeta.id, aliasColor)
				this._aliasColorScheme.getBatchColorPreset().add(aliasColor);
			}
			await this.#ioc.getSubstanceRepository().addSubstanceToInjections(req);
			this._reloadPage();
		} catch (e) {
			this.#setFullWindowProgressBarVisible(false);
			throw e;
		}
	}

	/**
	 * @param {StructureMetaList} structures
	 * @returns {Promise<void>}
	 */
	async #copyAnalytesIntoInjections(structures) {
		const injectionIds = await this._startSelectingInjections(structures.size());
		try {
			this.#setFullWindowProgressBarVisible(true);
			await this.#ioc.getSubstanceRepository()
				.copySubstances(structures.getSourceSubstanceIds(), injectionIds);
			this._reloadPage();
		} catch (e) {
			this.#setFullWindowProgressBarVisible(false);
			throw e;
		}
	}

	#setFullWindowProgressBarVisible(isVisible) {
		document.getElementById('full-window-progress-bar').style.display =
			isVisible ? 'block' : 'none';
	}

	#createBatchViewComponent() {
		const batchViewCmp = this.#ioc.getBatchViewCmp();
		batchViewCmp.init(document.querySelector('.plates'), {});
		batchViewCmp.onSelectionChange(this.#onPlateOrTableSelectionChange.bind(this));
		batchViewCmp.onViewModeChange((viewName) => {
			this.#ioc.getUserSettings().setUserDefinedBatchViewMode(viewName);
		});
		batchViewCmp.show();
	}

	/**
	 *
	 * @param {BatchMeta} batchMeta
	 * @param {Batch} batchData
	 */
	#setBatchViewCmpData(batchMeta, batchData) {
		const data = [];
		// Use only non-empty plates;
		// An empty plate could appear if the user doesn't have access to any of the injections in the plate.
		for (const plate of batchMeta.plates) {
			const injections = batchData.getInjectionsByPlateId(plate.id);
			if (Array.isArray(injections) && injections.length > 0)
				data.push({plate, injections});
		}
		let batchViewMode = this.#ioc.getUserSettings().getUserDefinedBatchViewMode();
		if (batchViewMode == null) {
			batchViewMode = data.length > BatchViewCmp.PLATES_THRESHOLD ?
				BatchViewCmp.TABLE_VIEW_MODE_NAME : BatchViewCmp.PLATES_VIEW_MODE_NAME;
		}
		const cmp = this.#ioc.getBatchViewCmp();
		cmp.setActiveView(batchViewMode);
		cmp.setData(data);
		// After injections are set, we can highlight plate wells or table rows.
		const literalCoordinates = extractHighlightedWellsLiteralCoordinatesFromUrl();
		if (literalCoordinates.length) {
			const added = cmp.convertCoordinatesToInjections(literalCoordinates);
			this.#onPlateOrTableSelectionChange({added});
		}
		cmp.setLoading(false);
	}
}

class SubstanceRequestCreator {
	constructor(batch, controller) {
		this.batch = batch;
		this.controller = controller;
	}

	/**
	 * @param {StructureMetaList} structureList
	 * @returns {Promise<void>}
	 */
	async create(structureList) {
		if (structureList.containsStructureWithEmptyInjectionName()){
			return this._processListWithEmptyInjectionName(structureList);
		} else {
			return this._processList(structureList);
		}
	}

	/**
	 * @param {StructureMetaList} structureList
	 * @returns {Promise<void>}
	 * @private
	 */
	async _processListWithEmptyInjectionName(structureList) {
		return new Promise(async (resolve, reject) => {
			if (structureList.getUniqueInjectionNamesCount() > 0) {
				const dialog = new BaseDialogCmp({title:"Warning", btnCancel: null});
				dialog.onOk(() => {
					reject(`Some structures don't have injection name associated with them.`);
				});
				dialog.showText(`Some structures don't have injection name associated with them.`);
			} else {
				try {
					const injectionIds = await this.controller._startSelectingInjections(structureList.size());
					const list = structureList.getStructures().map(s=>this._createStructureRequestObject(s, injectionIds));
					resolve(list);
				} catch (e){
					reject(e)
				}
			}
		});
	}

	/**
	 * @param {StructureMetaList} structureList
	 * @returns {Promise<void>}
	 * @private
	 */
	async _processList(structureList){
		let map = await this._mapStructureToInjectionIds(structureList);
		const list = [], allInjectionIds = new Set();
		map.forEach((ids,structure)=>{
			ids.forEach(id => allInjectionIds.add(id));
			list.push(this._createStructureRequestObject(structure, ids))
		})
		return new Promise((resolve, reject) => {
			if (list.length > 0){
				const dialog = new BaseDialogCmp();
				dialog.onOk(()=>resolve(list));
				dialog.onCancel(()=>reject());
				dialog.showText(`Add ${list.length} substances into ${allInjectionIds.size} injections?`);
			} else {
				reject('Nothing to add');
			}
		});
	}

	/**
	 * @param {StructureMeta} structure
	 * @param {string[]} injectionIds
	 * @returns {{injections : string[], substance: {SubstanceJson}}}
	 * @private
	 */
	_createStructureRequestObject(structure, injectionIds){
		return {
			injections: injectionIds,
			substance: structure.toSubstanceJson().toServerJson()
		}
	}

	/**
	 * @param {StructureMetaList} structureList
	 * @returns {Promise<Map<StructureMeta, string[]>>}
	 * @private
	 */
	async _mapStructureToInjectionIds(structureList) {
		const mapped = new Map(), skipped = new Set();

		for (const structure of structureList.getStructures()) {
			const injectionName = structure.injectionName;
			let ids = this.batch.getInjectionIdsByName(injectionName);
			if (!ids) {
				skipped.add(injectionName);
				continue;
			}
			mapped.set(structure, ids);
		}
		return new Promise((resolve, reject) => {
			if (skipped.size > 0) {
				const dialog = new BaseDialogCmp();
				dialog.onOk(() => resolve(mapped));
				dialog.onCancel(() => reject())
				dialog.showHtml(`
		            <main class="dialog__main-content">
						<p>${skipped.size} injection(s) aren't present in current batch, they will be skipped:</p>
						<ul>${Array.from(skipped).map(v => `<li>${safeText(v)}</li>`).join('')}</ul>
					</main>`);
			} else {
				resolve(mapped);
			}
		})
	}
}

/**
 * @deprecated
 * @see {BatchPageHeaderCmp}
 */
class BatchPageHeaderComponent {
	constructor(node) {
		this._root = node;
		this._callbacks = {};
		this._render();
		let underlay = null;

		this._root.querySelectorAll('.header-section .section-control').forEach(control=>{
			control.addEventListener('click', ()=>{
				const section = control.closest('.header-section');
				const dropdown = section.querySelector('.menu');
				underlay = MouseBlockingUnderlay.show(() => {
					dropdown.toggleAttribute('data-hidden');
				}, section);
				dropdown.toggleAttribute('data-hidden');
			})
		})

		this._root.querySelectorAll('.header-section li').forEach(li => {
			li.addEventListener('click', (_) => {
				const dropdown = li.closest('.menu');
				dropdown.toggleAttribute('data-hidden');
				underlay.remove();
				if (typeof this._callbacks[li.dataset.purpose] === 'function')
					this._callbacks[li.dataset.purpose]();
			})
		})

		this._root.querySelector('#open-injections-btn').addEventListener('click', () => {
			if (typeof this._callbacks['open-injections'] === 'function')
				this._callbacks['open-injections']();
		})
	}

	on(eventName, cb){
		this._callbacks[eventName] = cb;
	}

	updateOpenInjectionsButtonText(injectionsCount) {
		let text = injectionsCount > 0
			? `Open (${injectionsCount})`
			: 'Open all';
		this._root.querySelector('#open-injections-btn').textContent = text;
	}

	_render() {
		/** @type {HTMLElement} */
		const section3 = this._root.querySelector('.page-header__section--align-end .header-buttons-section');
		const html = ` <button id="open-injections-btn" class="button button--contained btn-ok"></button>
                          <div class="header-section add-visualization menu-caller">
                            <div tabindex="0" class="button section-control button--text"><span>Visualize</span><span class="span-triangle material-symbols-outlined">arrow_drop_down</span></div>
                            <menu class="menu menu--below" data-hidden> 
                              <li class="menu__item" data-purpose="add-visualization"> 
                              	<span class="add-visualization__item-title">
                              		<span class="material-symbols-outlined add-visualization__item-icon" style="width: 1rem">add</span>
                              		Add Table/Chart</span>
                              	<span class="menu__hotkey">v</span> </li>
                              <li class="menu__item" data-purpose="import-visualizations">
                              	<span class="add-visualization__item-title">
                              		<span class="material-symbols-outlined add-visualization__item-icon" style="width: 1rem">
                              		library_add</span>Import visualizations</span></li>
                            </menu>
                          </div>`;
		section3.insertAdjacentHTML('afterbegin', html);
	}
}
